import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsBellFill, BsTrash } from "react-icons/bs";
import { toast } from "react-toastify";
import { ApiPaths } from "../../api/paths";
import useOnClickOutside from "../../hooks/use-click-outside";
import useData from "../../hooks/use-data";
import useDisclosure from "../../hooks/use-disclosure";
import useMutateData from "../../hooks/use-mutate-data";
import { PaginatedApiResponseType } from "../../types/common";
import { NotificationType } from "../../types/notification";
import "./NotificationList.css";
import { useAppContext } from "../../context/app-context";

interface NotificationItemProps {
  notification: NotificationType;
  refetch: () => void;
}

function NotificationItem({ notification, refetch }: NotificationItemProps) {
  const { t } = useTranslation(["common"]);
  const [isRead, setIsRead] = useState(notification.isRead);
  const { mutate: deleteNotification } = useMutateData(
    `${ApiPaths.notifications}/${notification._id}`,
    {
      method: "DELETE",
      onError: () => {
        toast(t("error.fetch", { ns: "common", data: "Notifications" }), {
          type: "error",
          toastId: "notification-delete",
        });
      },
    }
  );
  const { mutate: updateNotification } = useMutateData(
    `${ApiPaths.notifications}/${notification._id}`,
    {
      method: "PUT",
      onSuccess: () => {
        setIsRead(true);
        refetch();
      },
    }
  );
  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    deleteNotification();
    refetch();
  };
  const handleUpdate = () => {
    if (!isRead) {
      updateNotification({ ...notification, isRead: true });
    }
  };
  return (
    <div
      className={`notification-body ${isRead ? "" : "notification-unread"}`}
      key={notification._id}
      onClick={handleUpdate}
    >
      <div className="me-3">
        <BsBellFill className={isRead ? "" : "text-danger"} />
      </div>
      <div>
        <h4 className="notification-title mb-1" style={{ fontSize: "14px" }}>
          {notification.title}
        </h4>
        <p className="notification-content">{notification.description}</p>
      </div>
      <button className="btn btn-outline-danger ms-3" onClick={handleDelete}>
        <BsTrash />
      </button>
    </div>
  );
}

export default function NotificationList() {
  const { show, onToggle, onHide } = useDisclosure();
  const { t } = useTranslation(["myProfile", "common"]);
  const notificationsRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(notificationsRef, onHide);
  const { keycloakProfile } = useAppContext();
  const [messageFilter, setMessageFilter] = useState("unread");
  const params = useMemo(
    () => ({
      params: {
        isRead:
          messageFilter === "read"
            ? true
            : messageFilter === "unread"
            ? false
            : undefined,
        email: keycloakProfile?.email,
      },
    }),
    [messageFilter]
  );
  const { data, error, refetch } = useData<
    PaginatedApiResponseType<NotificationType[]>
  >(ApiPaths.notifications, {
    options: params,
  });
  const hasUnread = useMemo(() => {
    let temp = [];
    if (data && Array.isArray(data)) {
      temp = data.filter((i) => !i.isRead);
    }
    return temp.length;
  }, [data]);
  const onChangeFilter = (filter: string) => {
    setMessageFilter(filter);
  };
  return (
    <div
      className="notifications-ref me-2 me-md-3 me-lg-4"
      ref={notificationsRef}
    >
      <div
        className={`profile-image profile-small`}
        onClick={onToggle}
        style={{ cursor: "pointer" }}
      >
        <BsBellFill
          className={`notification-icon ${hasUnread ? "text-danger" : ""}`}
        />
      </div>
      <div
        className={`notifications-container position-absolute top-100 bg-white rounded-2  border ${
          show ? "d-block" : "d-none"
        }`}
      >
        <div className="text-dark ">
          <div className=" notification-header p-3 w-100">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center w-100">
              <h3 className="font-16">
                {t("notifications.heading", { ns: "myProfile" })}
              </h3>
              <div className="d-flex justify-content-start align-items-center py-2 gap-2">
                {["all", "read", "unread"].map((item) => {
                  return (
                    <button
                      className={`btn btn-sm text-uppercase ${
                        messageFilter === item ? "btn-success" : "btn-secondary"
                      }`}
                      key={item}
                      onClick={() => onChangeFilter(item)}
                    >
                      {item}
                    </button>
                  );
                })}
              </div>
            </div>
            <button className="d-block d-md-none btn" onClick={onHide}>
              X
            </button>
          </div>
          <div className="notification-list">
            {data?.data && data?.data?.length > 0 ? (
              data?.data.map((notif) => {
                return (
                  <NotificationItem
                    notification={notif}
                    key={notif._id}
                    refetch={refetch}
                  />
                );
              })
            ) : (
              <>
                {error ? (
                  <span className="text-center px-3 py-3">
                    {t("error.fetch", { ns: "common", data: "Notifications" })}
                  </span>
                ) : (
                  <span className="text-center px-3 py-3">
                    {t("notifications.empty", { ns: "myProfile" })}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
